import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useParams} from 'react-router-dom'
import {Box, Card, CardContent, Chip, Skeleton, Typography} from '@mui/material'
import HighlightOffIcon   from '@mui/icons-material/HighlightOff'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {useForm} from 'react-hook-form'
import _ from 'lodash'
import {apiConstants, post} from '../../common/constants'
import {formatDate, isMerchantKycRejected, isMerchantKycApproved} from '../../common/util'
import {FormProvider, RHFSearchableSelectField} from '../../common/hook-form'
import {FieldLabel, FilterField} from '../../common/FilterBar'
import PageTitle from '../../components/PageTitle'
import MerchantDataTable from './MerchantDataTable'

// ─── Colour tokens (inline so extra-column renderCells are self-contained) ────
const CLR = {
  textSub:     '#475569',
  textMuted:   '#94a3b8',
  green:       '#15803d', greenBg:  '#f0fdf4', greenBorder: '#bbf7d0',
  amber:       '#b45309', amberBg:  '#fffbeb', amberBorder: '#fde68a',
  red:         '#b91c1c', redBg:    '#fef2f2', redBorder:   '#fecaca',
  sky:         '#0369a1', skyBg:    '#f0f9ff', skyBorder:   '#bae6fd',
}

// ─── View-specific extra columns ─────────────────────────────────────────────

const NOT_STARTED_COLS = [
  {
    field: 'daysSinceReg', headerName: 'Days Pending',
    minWidth: 120, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => {
      const d = row.daysSinceReg
      if (d == null) return <Typography sx={{fontSize: '12px', color: CLR.textMuted}}>—</Typography>
      const [bg, color, border] =
        d > 30 ? [CLR.redBg,   CLR.red,   CLR.redBorder]   :
        d > 14 ? [CLR.amberBg, CLR.amber, CLR.amberBorder] :
                 [CLR.skyBg,   CLR.sky,   CLR.skyBorder]
      return (
        <Box sx={{px: 1.2, py: 0.3, borderRadius: '6px', backgroundColor: bg, border: `1px solid ${border}`}}>
          <Typography sx={{fontSize: '11px', fontWeight: 700, color}}>{d}d</Typography>
        </Box>
      )
    },
  },
]

const AWAITING_REVIEW_COLS = [
  {
    field: 'kycDetails', headerName: 'Docs Submitted',
    minWidth: 120, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => (
      <Typography sx={{fontSize: '13px', fontWeight: 600}}>
        {(row.kycDetails || []).length}
      </Typography>
    ),
  },
  {
    field: 'submittedDate', headerName: 'Submitted On',
    minWidth: 130, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => (
      <Typography sx={{fontSize: '12px', color: CLR.textSub}}>
        {row.submittedDate ? formatDate(row.submittedDate) : '—'}
      </Typography>
    ),
  },
  {
    field: 'daysWaiting', headerName: 'Waiting',
    minWidth: 90, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => {
      const d = row.daysWaiting
      if (d == null) return <Typography sx={{fontSize: '12px', color: CLR.textMuted}}>—</Typography>
      const [bg, color, border] =
        d > 14 ? [CLR.redBg,   CLR.red,   CLR.redBorder]   :
        d > 7  ? [CLR.amberBg, CLR.amber, CLR.amberBorder] :
                 [CLR.greenBg, CLR.green, CLR.greenBorder]
      return (
        <Box sx={{px: 1.2, py: 0.3, borderRadius: '6px', backgroundColor: bg, border: `1px solid ${border}`}}>
          <Typography sx={{fontSize: '11px', fontWeight: 700, color}}>{d}d</Typography>
        </Box>
      )
    },
  },
]

const DOCS_REJECTED_COLS = [
  {
    field: 'rejectedDocs', headerName: 'Rejected Documents',
    minWidth: 200, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => {
      const rejected = (row.kycDetails || []).filter((d) => d.rejected === true)
      if (!rejected.length)
        return <Typography sx={{fontSize: '12px', color: CLR.textMuted}}>—</Typography>
      return (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5}}>
          {rejected.map((d, i) => (
            <Chip
              key={i}
              icon={<HighlightOffIcon sx={{fontSize: '13px !important'}} />}
              label={d.kycDocName}
              size='small'
              variant='outlined'
              sx={{
                fontSize: '10px', height: 20,
                borderColor: CLR.redBorder, color: CLR.red,
                '& .MuiChip-icon': {color: CLR.red},
              }}
            />
          ))}
        </Box>
      )
    },
  },
  {
    field: 'rejectedBy', headerName: 'Rejected By',
    minWidth: 130, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => {
      const names = [...new Set(
        (row.kycDetails || [])
          .filter((d) => d.rejected)
          .map((d) => d.verifiedBy || d.rejectedBy)
          .filter(Boolean)
      )]
      return <Typography sx={{fontSize: '12px', color: CLR.textSub}}>{names.join(', ') || '—'}</Typography>
    },
  },
  {
    field: 'rejectedAt', headerName: 'Rejected At',
    minWidth: 130, align: 'left', headerAlign: 'left', hideSortIcons: true,
    renderCell: ({row}) => {
      const dates = (row.kycDetails || [])
        .filter((d) => d.rejected && (d.verifiedDate || d.rejectedDate))
        .map((d) => d.verifiedDate || d.rejectedDate)
        .sort().reverse()
      return (
        <Typography sx={{fontSize: '12px', color: CLR.textSub}}>
          {dates.length ? formatDate(dates[0]) : '—'}
        </Typography>
      )
    },
  },
]

// ─── View → config ────────────────────────────────────────────────────────────
const VIEW_CONFIG = {
  'not-started': {
    title:        'KYC Not Started',
    description:  'Merchants who completed registration but have not uploaded any KYC documents yet.',
    icon:         <HourglassEmptyIcon />,
    severity:     'warning',
    color:        CLR.amber, bg: CLR.amberBg, border: CLR.amberBorder,
    filterFn:     (m) => m.status === 'KYC_PENDING',
    extraColumns: NOT_STARTED_COLS,
  },
  'awaiting-review': {
    title:        'Awaiting Review',
    description:  'Merchants who have uploaded KYC documents and are waiting for admin review.',
    icon:         <PendingActionsIcon />,
    severity:     'info',
    color:        CLR.sky, bg: CLR.skyBg, border: CLR.skyBorder,
    filterFn:     (m) => m.status === 'KYC_UPLOADED',
    extraColumns: AWAITING_REVIEW_COLS,
  },
  'docs-rejected': {
    title:        'KYC Rejected',
    description:  'Merchants whose KYC has been rejected or has at least one rejected document.',
    icon:         <HighlightOffIcon />,
    severity:     'error',
    color:        CLR.red, bg: CLR.redBg, border: CLR.redBorder,
    filterFn:     isMerchantKycRejected,
    extraColumns: DOCS_REJECTED_COLS,
  },
  'approved': {
    title:        'KYC Approved & Live',
    description:  'Merchants who have passed full KYC verification and are active on the platform.',
    icon:         <CheckCircleOutlineIcon />,
    severity:     'success',
    color:        CLR.green, bg: CLR.greenBg, border: CLR.greenBorder,
    filterFn:     isMerchantKycApproved,
    extraColumns: [],
  },
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function KycMerchantList() {
  const {view}  = useParams()
  const config  = VIEW_CONFIG[view]

  const methods = useForm({defaultValues: {merchant: ''}})
  const {reset, watch} = methods
  const selectedMerchant = watch('merchant')

  const [rawData,    setRawData]    = useState([])
  const [loading,    setLoading]    = useState(false)
  const [page,       setPage]       = useState(0)
  const [pageSize,   setPageSize]   = useState(25)
  const [reload,     setReload]     = useState(0)

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(() => {
    setLoading(true)
    post(apiConstants.MERCHANT_SEARCH, {
      offset: 0,
      limit: 10000,
      andfilter: [],
      includes: 'name,status,merchantId,id,address,city,state,pincode,country,companyName,is_deleted,test_merchant,isTestMerchant,kycDetails,createdDate,merchantSignedDoc,codRemittanceConfig',
    })
      .then((res) => setRawData(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {fetchData()}, [fetchData, reload])

  // ── Enrich + filter ────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (!config || !rawData.length) return []
    const now    = Date.now()
    const active = rawData.filter((m) => m.is_deleted !== '1' && !m.test_merchant && !m.isTestMerchant)
    const list   = active.filter(config.filterFn)

    if (view === 'not-started') {
      return list
        .map((m) => ({
          ...m,
          daysSinceReg: m.createdDate
            ? Math.floor((now - new Date(m.createdDate).getTime()) / 86400000)
            : null,
        }))
        .sort((a, b) => (b.daysSinceReg ?? 0) - (a.daysSinceReg ?? 0))
    }

    if (view === 'awaiting-review') {
      return list
        .map((m) => {
          const uploadTs = (m.kycDetails || []).filter((d) => d.uploadDate).map((d) => new Date(d.uploadDate).getTime())
          const earliest = uploadTs.length ? Math.min(...uploadTs) : null
          return {
            ...m,
            daysWaiting:   earliest ? Math.floor((now - earliest) / 86400000) : null,
            submittedDate: earliest ? new Date(earliest).toISOString() : null,
          }
        })
        .sort((a, b) => (b.daysWaiting ?? 0) - (a.daysWaiting ?? 0))
    }

    if (view === 'approved') {
      return list
        .map((m) => {
          const docs       = m.kycDetails || []
          const verifiedTs = docs.filter((d) => d.verifiedDate).map((d) => new Date(d.verifiedDate).getTime())
          const uploadTs   = docs.filter((d) => d.uploadDate).map((d) => new Date(d.uploadDate).getTime())
          let daysToApproval = null, approvalDate = null
          if (verifiedTs.length && uploadTs.length) {
            const days = (Math.max(...verifiedTs) - Math.min(...uploadTs)) / 86400000
            if (days >= 0 && days < 365) daysToApproval = parseFloat(days.toFixed(1))
            approvalDate = new Date(Math.max(...verifiedTs)).toISOString()
          }
          return {...m, daysToApproval, approvalDate}
        })
        .sort((a, b) => (b.daysToApproval ?? 0) - (a.daysToApproval ?? 0))
    }

    return list // docs-rejected — no extra enrichment
  }, [rawData, view, config])

  const displayRows = useMemo(() => {
    if (!selectedMerchant) return rows
    return rows.filter((r) => r.id === selectedMerchant)
  }, [rows, selectedMerchant])

  // ── Unknown view guard ─────────────────────────────────────────────────────
  if (!config) {
    return (
      <Box sx={{py: 6, textAlign: 'center'}}>
        <Typography sx={{fontSize: '0.9rem', color: CLR.textMuted}}>Unknown KYC view: {view}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>

      {/* ── Page header ── */}
      <PageTitle>{config.title}</PageTitle>

      {/* ── Filter bar ── */}
      <Card sx={{boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px'}}>
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <FormProvider methods={methods}>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end'}}>
              <FilterField sx={{maxWidth: 400}}>
                <FieldLabel>Search Merchant</FieldLabel>
                <RHFSearchableSelectField
                  fullWidth
                  name='merchant'
                  label=''
                  options={_.orderBy(rows, 'name').map((v) => ({
                    value: v.id,
                    label: v.companyName ? `${v.name} (${v.companyName})` : v.name,
                  }))}
                />
              </FilterField>
              <Box
                component='button' type='button'
                onClick={() => reset({merchant: ''})}
                sx={{
                  fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px',
                  border: 'none', cursor: 'pointer',
                  bgcolor: '#ef4444', color: '#fff', '&:hover': {bgcolor: '#dc2626'},
                }}
              >
                Reset
              </Box>
            </Box>
          </FormProvider>
        </CardContent>
      </Card>

      {/* ── Info banner ── */}
      {!loading && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.25, borderRadius: '10px',
          backgroundColor: config.bg, border: `1px solid ${config.border}`,
        }}>
          {React.cloneElement(config.icon, {sx: {fontSize: 20, color: config.color, flexShrink: 0}})}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Box sx={{
              px: 1, py: 0.2, borderRadius: '6px', flexShrink: 0,
              backgroundColor: config.color, color: '#fff',
              fontSize: '0.72rem', fontWeight: 800, lineHeight: '18px',
            }}>
              {displayRows.length} merchant{displayRows.length !== 1 ? 's' : ''}
            </Box>
            <Typography sx={{fontSize: '0.8rem', color: '#475569', lineHeight: 1.5}}>
              {config.description}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <Box sx={{bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9',
          }}>
            {[160, 140, 200, 100, 80, 90, 100].map((w, i) => (
              <Skeleton key={i} variant='rounded' width={w} height={12} sx={{flexShrink: 0}} />
            ))}
          </Box>
          {/* Rows */}
          {Array.from({length: 10}).map((_, r) => (
            <Box key={r} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2, py: 1.6, borderBottom: '1px solid #f8fafc',
            }}>
              <Skeleton variant='rounded' width={160} height={13} sx={{flexShrink: 0}} />
              <Box sx={{flexShrink: 0, width: 140}}>
                <Skeleton variant='rounded' width={140} height={13} sx={{mb: 0.6}} />
                <Skeleton variant='rounded' width={100} height={11} />
              </Box>
              <Skeleton variant='rounded' width={200} height={13} sx={{flexShrink: 0}} />
              <Skeleton variant='rounded' width={100} height={13} sx={{flexShrink: 0}} />
              <Skeleton variant='rounded' width={80}  height={13} sx={{flexShrink: 0}} />
              <Skeleton variant='rounded' width={90}  height={24} sx={{flexShrink: 0, borderRadius: '4px'}} />
              <Skeleton variant='rounded' width={100} height={13} sx={{flexShrink: 0}} />
            </Box>
          ))}
        </Box>
      )}

      {/* ── Table ── */}
      {!loading && (
        <MerchantDataTable
          rows={displayRows}
          page={page}
          setPage={setPage}
          sizePerPage={pageSize}
          setSizePerPage={(s) => {setPageSize(s); setPage(0)}}
          rowCount={displayRows.length}
          reload={reload}
          setReload={setReload}
          extraColumns={config.extraColumns}
        />
      )}

      {!loading && displayRows.length === 0 && (
        <Box sx={{py: 7, textAlign: 'center'}}>
          <Typography sx={{fontSize: '0.85rem', color: CLR.textMuted, fontWeight: 500}}>
            No merchants found for this status.
          </Typography>
        </Box>
      )}

    </Box>
  )
}
